import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Drawer,
  Group,
  Modal,
  Progress,
  SegmentedControl,
  Stack,
  Text,
  Textarea,
  useMantineTheme,
} from "@mantine/core";
import {
  ArrowLeft,
  Redo2,
  Undo2,
  Download,
  Upload,
  RotateCcw,
  Save,
  Eye,
  X,
} from "lucide-react";
import StudioEditor from "@grapesjs/studio-sdk/react";
import "@grapesjs/studio-sdk/style";
import type { Editor } from "grapesjs";
import { Endpoints } from "../../../../lib/endpoints";
import { DashboardEndpoints } from "../../../../lib/endpoints-dashboard";
import { PerformRequest } from "../../../../lib/usePerformRequest";

type ViewportMode = "desktop" | "tablet" | "mobile";

const studioCss = ``;

const GrapesPageBuilder: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const orgId = id || "";
  const navigate = useNavigate();
  const theme = useMantineTheme();

  const editorRef = useRef<Editor | null>(null);

  const [viewport, setViewport] = useState<ViewportMode>("desktop");
  const [importValue, setImportValue] = useState<string>("");
  const [exportValue, setExportValue] = useState<string>("");
  const [isDataPanelOpen, setIsDataPanelOpen] = useState<boolean>(false);
  const [isPublishModalOpen, setIsPublishModalOpen] = useState<boolean>(false);
  const [isPublishing, setIsPublishing] = useState<boolean>(false);
  const [publishProgress, setPublishProgress] = useState<number>(0);
  const [publishError, setPublishError] = useState<string>("");
  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);
  const [previewSrcDoc, setPreviewSrcDoc] = useState<string>("");

  const draftKey = useMemo(() => `grapes_draft_org_${orgId}`, [orgId]);
  const publishedKey = useMemo(() => `grapes_published_org_${orgId}`, [orgId]);

  const applyViewport = (ed: Editor, mode: ViewportMode) => {
    if (mode === "desktop") ed.setDevice("Desktop");
    if (mode === "tablet") ed.setDevice("Tablet");
    if (mode === "mobile") ed.setDevice("Mobile");
  };

  const safeParseJson = (value: string) => {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  };

  const saveDraft = () => {
    const ed = editorRef.current;
    if (!ed) return;
    const data = ed.getProjectData();
    localStorage.setItem(draftKey, JSON.stringify(data));
  };

  const loadDraft = () => {
    const ed = editorRef.current;
    if (!ed) return;
    const raw = localStorage.getItem(draftKey);
    if (!raw) return;
    const data = safeParseJson(raw);
    if (!data) return;
    ed.loadProjectData(data);
  };

  const publish = () => {
    const ed = editorRef.current;
    if (!ed) return;
    const project = ed.getProjectData();
    const html = ed.getHtml();
    const css = ed.getCss();

    const assets = (() => {
      try {
        const raw = localStorage.getItem(assetsKey);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    })();

    const payload = {
      orgId,
      schemaVersion: 1,
      updatedAt: new Date().toISOString(),
      projectType: "web",
      storage: {
        project: {
          purpose: "Resume editing later (Studio SDK)",
          data: project,
        },
        render: {
          purpose: "Render on client (fast, no editor runtime)",
          html,
          css,
        },
        assets: {
          purpose: "Asset manifest (optional but recommended)",
          items: assets,
        },
      },
      project,
      html,
      css,
      assets,
    };

    console.log(
      "[PageBuilder Backend Save Payload] Store this payload on your backend to render AND resume editing with no data loss",
      payload
    );
    localStorage.setItem(publishedKey, JSON.stringify(project));

    return payload;
  };

  const confirmPublish = async () => {
    if (isPublishing) return;
    setPublishError("");
    setIsPublishing(true);
    setPublishProgress(8);

    const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));
    let timer: number | undefined;

    try {
      timer = window.setInterval(() => {
        setPublishProgress((p) => {
          if (p >= 92) return p;
          const next = p + Math.max(1.5, Math.random() * 7);
          return Math.min(92, Math.round(next));
        });
      }, 160);

      await delay(450);
      const payload = publish();
      if (!payload) throw new Error("Editor not ready");

      const organizationId = Number(orgId);
      if (!Number.isFinite(organizationId)) {
        throw new Error("Invalid organization id");
      }

      const { route, method } =
        DashboardEndpoints.GetSetOrganizationHomepageJSON();
      const apiPayload = {
        organization_id: organizationId,
        style_data: JSON.stringify(payload),
      };

      await delay(250);
      setPublishProgress(94);

      const response = await PerformRequest<any>({
        method,
        route,
        data: apiPayload,
      });

      if (!response?.status || response.status >= 400) {
        throw new Error("Publish request failed");
      }

      await delay(350);
      setPublishProgress(100);
      await delay(450);
      setIsPublishModalOpen(false);
    } catch {
      setPublishError("Publish failed. Please try again.");
    } finally {
      if (timer) window.clearInterval(timer);
      setIsPublishing(false);
      setPublishProgress(0);
    }
  };

  const loadPublished = () => {
    const ed = editorRef.current;
    if (!ed) return;
    const raw = localStorage.getItem(publishedKey);
    if (!raw) return;
    const data = safeParseJson(raw);
    if (!data) return;
    ed.loadProjectData(data);
  };

  const reset = () => {
    const ed = editorRef.current;
    if (!ed) return;
    ed.DomComponents.clear();
    ed.CssComposer.clear();
    ed.setStyle("");
  };

  const exportProject = () => {
    const ed = editorRef.current;
    if (!ed) return;
    const html = ed.getHtml();
    const css = ed.getCss();
    const project = ed.getProjectData();
    setExportValue(JSON.stringify({ html, css, project }, null, 2));
  };

  const openPreview = () => {
    const ed = editorRef.current;
    if (!ed) return;
    const html = ed.getHtml();
    const css = ed.getCss();
    const doc = `<!doctype html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>Preview</title>
    <style>
      html, body { margin: 0; padding: 0; height: 100%; }
      body { font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji"; }
      ${css}
    </style>
  </head>
  <body>
    ${html}
  </body>
</html>`;
    setPreviewSrcDoc(doc);
    setIsPreviewOpen(true);
  };

  const importProject = () => {
    const ed = editorRef.current;
    if (!ed) return;

    const parsed = safeParseJson(importValue);
    if (parsed?.project) {
      ed.loadProjectData(parsed.project);
      return;
    }

    if (parsed?.html || parsed?.css) {
      ed.DomComponents.clear();
      ed.CssComposer.clear();
      ed.setComponents(parsed.html || "");
      ed.setStyle(parsed.css || "");
    }
  };

  useEffect(() => {
    return () => {
      editorRef.current = null;
    };
  }, []);

  useEffect(() => {
    const ed = editorRef.current;
    if (!ed) return;
    applyViewport(ed, viewport);
  }, [viewport]);

  const { route: uploadRoute, method: uploadMethod } =
    Endpoints.GetUploadFile();

  const assetsKey = useMemo(() => `grapes_assets_org_${orgId}`, [orgId]);

  const findCandidateUrl = (value: any, depth = 0): string | null => {
    if (depth > 6) return null;
    if (typeof value === "string") {
      const s = value.trim();
      if (!s) return null;
      if (/^https?:\/\//i.test(s)) return s;
      if (s.startsWith("/")) return s;
      if (/\.(png|jpe?g|gif|webp|svg)(\?.*)?$/i.test(s)) return s;
      return null;
    }
    if (Array.isArray(value)) {
      for (const item of value) {
        const found = findCandidateUrl(item, depth + 1);
        if (found) return found;
      }
      return null;
    }
    if (value && typeof value === "object") {
      const preferredKeys = [
        "url",
        "secure_url",
        "image",
        "imageUrl",
        "file",
        "fileUrl",
        "location",
        "path",
        "src",
      ];
      for (const k of preferredKeys) {
        if (k in value) {
          const found = findCandidateUrl((value as any)[k], depth + 1);
          if (found) return found;
        }
      }
      for (const k of Object.keys(value)) {
        const found = findCandidateUrl((value as any)[k], depth + 1);
        if (found) return found;
      }
    }
    return null;
  };

  const normalizeUrl = (candidateRaw: string) => {
    try {
      const origin = new URL(uploadRoute).origin;
      return /^https?:\/\//i.test(candidateRaw)
        ? candidateRaw
        : candidateRaw.startsWith("/")
        ? `${origin}${candidateRaw}`
        : `${origin}/${candidateRaw}`;
    } catch {
      return candidateRaw;
    }
  };

  const uploadSingleFile = async (file: File): Promise<string | null> => {
    const form = new FormData();
    form.append("file", file);
    form.append("image", file);
    form.append("files", file);

    const r = await PerformRequest<any>({
      method: uploadMethod as any,
      route: uploadRoute,
      data: form,
      headers: { "Content-Type": "multipart/form-data" },
    });

    const axiosResponseLike = (r as any)?.data;
    const body = axiosResponseLike?.data ?? axiosResponseLike;
    const candidateRaw =
      findCandidateUrl(body) || findCandidateUrl(axiosResponseLike);
    return candidateRaw ? normalizeUrl(candidateRaw) : null;
  };

  const readAssets = () => {
    try {
      const raw = localStorage.getItem(assetsKey);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const writeAssets = (assets: any[]) => {
    try {
      localStorage.setItem(assetsKey, JSON.stringify(assets));
    } catch {
      // ignore
    }
  };

  if (!orgId) {
    return (
      <div style={{ padding: 24, color: "#0f172a" }}>
        Missing organization id
      </div>
    );
  }

  const licenseKey =
    (process.env.REACT_APP_GRAPESJS_STUDIO_LICENSE_KEY as string | undefined) ||
    "DEV_LICENSE_KEY";

  return (
    <div
      className="gjs-studio-shell"
      style={{ height: "100vh", display: "flex", flexDirection: "column" }}
    >
      <style>{studioCss}</style>
      <div
        className="gjs-studio-topbar"
        style={{
          height: 56,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 12px",
          borderBottom: "1px solid rgba(148, 163, 184, 0.14)",
          backgroundColor: "#0f1629",
          position: "sticky",
          top: 0,
          zIndex: 1,
        }}
      >
        [...]
        <Group gap="sm">
          <Button
            variant="subtle"
            leftSection={<ArrowLeft size={18} />}
            onClick={() => navigate(`/d/organizations/${orgId}`)}
            size="sm"
          >
            Back
          </Button>
          <div style={{ fontSize: 13, fontWeight: 800, color: "#e5e7eb" }}>
            Studio SDK Page Builder
          </div>
        </Group>
        <Group gap="sm">
          <SegmentedControl
            size="xs"
            value={viewport}
            onChange={(v) => setViewport(v as ViewportMode)}
            data={[
              { label: "Desktop", value: "desktop" },
              { label: "Tablet", value: "tablet" },
              { label: "Mobile", value: "mobile" },
            ]}
          />

          <Button
            variant="light"
            size="xs"
            leftSection={<Undo2 size={14} />}
            onClick={() => editorRef.current?.Commands.run("core:undo")}
          >
            Undo
          </Button>
          <Button
            variant="light"
            size="xs"
            leftSection={<Redo2 size={14} />}
            onClick={() => editorRef.current?.Commands.run("core:redo")}
          >
            Redo
          </Button>

          <Button
            variant="light"
            size="xs"
            leftSection={<Download size={14} />}
            onClick={() => setIsDataPanelOpen(true)}
          >
            Import/Export
          </Button>

          <Button
            variant="default"
            size="xs"
            leftSection={<Save size={14} />}
            onClick={saveDraft}
          >
            Save Draft
          </Button>
          <Button variant="default" size="xs" onClick={loadDraft}>
            Load Draft
          </Button>
          <Button
            variant="filled"
            size="xs"
            leftSection={<Eye size={14} />}
            onClick={() => setIsPublishModalOpen(true)}
          >
            Publish
          </Button>
          <Button
            variant="light"
            size="xs"
            leftSection={<Eye size={14} />}
            onClick={openPreview}
          >
            Preview
          </Button>
          <Button variant="default" size="xs" onClick={loadPublished}>
            Load Published
          </Button>

          <Button
            variant="light"
            size="xs"
            leftSection={<RotateCcw size={14} />}
            color="red"
            onClick={reset}
          >
            Reset
          </Button>
        </Group>
      </div>

      {isPreviewOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            background: "rgba(2, 6, 23, 0.92)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              height: 56,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "0 12px",
              borderBottom: "1px solid rgba(148, 163, 184, 0.14)",
              background: "rgba(15, 22, 41, 0.86)",
              backdropFilter: "blur(10px)",
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 800, color: "#e5e7eb" }}>
              Preview
            </div>
            <Group gap="xs">
              <Button
                variant="light"
                size="xs"
                onClick={() => setIsPreviewOpen(false)}
                leftSection={<X size={14} />}
              >
                Close
              </Button>
            </Group>
          </div>
          <div style={{ flex: 1, minHeight: 0, padding: 12 }}>
            <iframe
              title="Grapes Preview"
              sandbox="allow-forms allow-modals allow-pointer-lock allow-popups allow-same-origin"
              style={{
                width: "100%",
                height: "100%",
                border: "1px solid rgba(148, 163, 184, 0.14)",
                borderRadius: 12,
                background: "#ffffff",
              }}
              srcDoc={previewSrcDoc}
            />
          </div>
        </div>
      )}

      <div style={{ flex: 1, minHeight: 0 }}>
        <StudioEditor
          style={{ width: "100%", height: "100%" }}
          options={{
            licenseKey,
            project: {
              type: "web",
              default: {
                pages: [{ name: "Homepage", component: "<main></main>" }],
              },
            },
            devices: {
              default: [
                { id: "desktop", name: "Desktop", width: "" },
                { id: "tablet", name: "Tablet", width: "768px" },
                { id: "mobile", name: "Mobile", width: "375px" },
              ],
              selected:
                viewport === "mobile"
                  ? "mobile"
                  : viewport === "tablet"
                  ? "tablet"
                  : "desktop",
            },
            storage: {
              type: "self",
              autosaveIntervalMs: 800,
              onLoad: async () => {
                const raw = localStorage.getItem(draftKey);
                const project = raw ? safeParseJson(raw) : null;
                return {
                  id: orgId,
                  project: project || {
                    pages: [{ name: "Homepage", component: "<main></main>" }],
                  },
                };
              },
              onSave: async ({ project }) => {
                localStorage.setItem(draftKey, JSON.stringify(project));
              },
            },
            assets: {
              storageType: "self",
              onLoad: async () => {
                return readAssets();
              },
              onUpload: async ({ files }) => {
                const uploaded = [] as any[];

                for (const file of files) {
                  try {
                    const url = await uploadSingleFile(file);
                    if (!url) continue;
                    uploaded.push({ src: url, name: file.name });
                  } catch {
                    // ignore
                  }
                }

                if (uploaded.length) {
                  const existing = readAssets();
                  writeAssets([...uploaded, ...existing]);
                }

                return uploaded;
              },
            },
            onEditor: (editor) => {
              editorRef.current = editor;
              applyViewport(editor, viewport);
            },
            onDestroy: () => {
              editorRef.current = null;
            },
          }}
        />
      </div>

      <Modal
        opened={isPublishModalOpen}
        onClose={() => {
          if (!isPublishing) setIsPublishModalOpen(false);
        }}
        centered
        title={<Text fw={800}>Publish homepage</Text>}
        overlayProps={{ backgroundOpacity: 0.65, blur: 6 }}
        radius="lg"
        padding="lg"
        closeOnEscape={!isPublishing}
        closeOnClickOutside={!isPublishing}
        withCloseButton={!isPublishing}
      >
        <Stack gap="sm">
          <Text size="sm" c="dimmed">
            This will publish the latest version of your organization homepage.
          </Text>

          {publishError && (
            <Text size="sm" c="red" fw={600}>
              {publishError}
            </Text>
          )}

          {isPublishing ? (
            <Stack gap={6}>
              <Text size="sm" fw={700}>
                Publishing...
              </Text>
              <Progress
                value={publishProgress}
                striped
                animated
                color={theme.primaryColor}
                radius="xl"
                size="lg"
              />
              <Text size="xs" c="dimmed">
                Please don’t close this window.
              </Text>
            </Stack>
          ) : (
            <Group justify="space-between" mt="xs">
              <Button
                variant="default"
                onClick={() => setIsPublishModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                variant="filled"
                color={theme.primaryColor}
                onClick={confirmPublish}
              >
                Confirm publish
              </Button>
            </Group>
          )}
        </Stack>
      </Modal>

      <Drawer
        opened={isDataPanelOpen}
        onClose={() => setIsDataPanelOpen(false)}
        position="right"
        size="md"
        title="Import / Export"
        overlayProps={{ backgroundOpacity: 0.55, blur: 4 }}
      >
        <Group gap="xs" style={{ marginBottom: 12 }}>
          <Button
            variant="light"
            size="xs"
            leftSection={<Download size={14} />}
            onClick={exportProject}
          >
            Export
          </Button>
          <Button
            variant="light"
            size="xs"
            leftSection={<Upload size={14} />}
            onClick={importProject}
          >
            Import
          </Button>
        </Group>
        <Textarea
          value={importValue}
          onChange={(e) => setImportValue(e.target.value)}
          placeholder='Paste JSON: { "project": ... } or { "html": "...", "css": "..." }'
          minRows={8}
          autosize
        />
        <div style={{ height: 12 }} />
        <Textarea
          value={exportValue}
          onChange={(e) => setExportValue(e.target.value)}
          placeholder="Export will appear here"
          minRows={10}
          autosize
        />
      </Drawer>
    </div>
  );
};

export default GrapesPageBuilder;
